// Redaction helpers for client-side logs. Mirrors backend LogSanitizer /
// ArgumentSanitizer rules so frontend + backend logs use the same masking
// scheme — same shapes are searchable by the same query.
//
// Defense-in-depth: this layer is NOT the primary defense. Callers must avoid
// passing PII in the first place (see "Do NOT Log" checklist in
// docs/logging/FRONTEND_LOGGING_ENTERPRISE_PLAN.md §12).

// ---- Constants ------------------------------------------------------------

const DROP_HEADERS: ReadonlySet<string> = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
]);

// Keys whose VALUES must always be redacted, regardless of nesting depth.
const REDACT_KEY_RE = /password|otp|secret|apikey|signature|cvv|cardnumber|^token$|accesstoken|refreshtoken|bearer/i;

// URI path fragments that indicate the entire body should be dropped.
const FULL_DROP_URI_FRAGMENTS = ['/login', '/password', '/otp', '/refresh'];

const MAX_STRING_LEN = 2048;
const MAX_OBJECT_DEPTH = 6;

const REDACTED = '[REDACTED]';
const REDACTED_EMAIL = '[REDACTED_EMAIL]';

// ---- Primitive maskers ----------------------------------------------------

export function maskEmail(email: string | null | undefined): string | null | undefined {
  if (email == null) return email;
  const e = email.trim();
  if (!e) return email;
  const at = e.indexOf('@');
  if (at <= 1) return REDACTED_EMAIL;
  return `${e.charAt(0)}***${e.slice(at)}`;
}

export function maskPhone(phone: string | null | undefined): string | null | undefined {
  if (phone == null) return phone;
  const p = phone.trim();
  if (!p) return phone;
  const keep = Math.min(4, p.length);
  return '*'.repeat(Math.max(0, p.length - keep)) + p.slice(p.length - keep);
}

export function maskId(id: string | null | undefined): string | null | undefined {
  if (id == null) return id;
  const s = id.trim();
  if (!s) return id;
  const keep = Math.min(6, s.length);
  return `***${s.slice(s.length - keep)}`;
}

export function truncate(value: string | null | undefined, maxLen: number = MAX_STRING_LEN): string | null | undefined {
  if (value == null) return value;
  if (value.length <= maxLen) return value;
  return value.slice(0, Math.max(0, maxLen)) + '...(truncated)';
}

// ---- Detectors ------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Loose phone matcher: 8–15 digits with optional + and separators.
const PHONE_RE = /^\+?[0-9][0-9\s\-().]{6,14}$/;
// JWT: 3 base64url segments separated by dots.
const JWT_RE = /^[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}$/;

function looksLikeEmail(v: string): boolean {
  return EMAIL_RE.test(v);
}
function looksLikePhone(v: string): boolean {
  return PHONE_RE.test(v.replace(/\s/g, ''));
}
function looksLikeJwt(v: string): boolean {
  return JWT_RE.test(v);
}
function looksLikeBearer(v: string): boolean {
  return /^bearer\s+/i.test(v);
}

// ---- Headers --------------------------------------------------------------

export function sanitizeHeaders(headers: Record<string, string | null | undefined> | null | undefined): Record<string, string | null> {
  if (!headers) return {};
  const out: Record<string, string | null> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k == null) continue;
    if (DROP_HEADERS.has(k.toLowerCase())) {
      out[k] = REDACTED;
      continue;
    }
    out[k] = v == null ? null : (truncate(v, 512) as string);
  }
  return out;
}

// ---- Request body (URI-aware) --------------------------------------------

export function sanitizeRequestBody(uri: string | null | undefined, body: string | null | undefined): string | null {
  if (body == null) return null;
  if (body.trim() === '') return null;
  const u = (uri ?? '').toLowerCase();
  if (FULL_DROP_URI_FRAGMENTS.some((frag) => u.includes(frag))) {
    return REDACTED;
  }
  let redacted = body;
  // Same regex set as backend LogSanitizer.
  redacted = redacted.replace(/(?:"password"\s*:\s*")([^"]*)(")/gi, '"password":"[REDACTED]"');
  redacted = redacted.replace(/(?:"token"\s*:\s*")([^"]*)(")/gi, '"token":"[REDACTED]"');
  redacted = redacted.replace(/(?:"otp"\s*:\s*")([^"]*)(")/gi, '"otp":"[REDACTED]"');
  redacted = redacted.replace(/(?:"secret"\s*:\s*")([^"]*)(")/gi, '"secret":"[REDACTED]"');
  redacted = redacted.replace(/(?:"apiKey"\s*:\s*")([^"]*)(")/gi, '"apiKey":"[REDACTED]"');
  redacted = redacted.replace(/(?:"signature"\s*:\s*")([^"]*)(")/gi, '"signature":"[REDACTED]"');
  return truncate(redacted, MAX_STRING_LEN) ?? null;
}

// ---- Generic deep object sanitizer ---------------------------------------

function sanitizeString(value: string, keyHint?: string): string {
  if (keyHint && REDACT_KEY_RE.test(keyHint)) return REDACTED;
  if (looksLikeBearer(value) || looksLikeJwt(value)) return REDACTED;
  if (keyHint && /email/i.test(keyHint) && looksLikeEmail(value)) return maskEmail(value) as string;
  if (keyHint && /(phone|mobile|msisdn)/i.test(keyHint) && looksLikePhone(value)) return maskPhone(value) as string;
  // If a string value happens to be an email/phone but the key doesn't say so,
  // we still mask it — defensive default.
  if (looksLikeEmail(value)) return maskEmail(value) as string;
  if (looksLikePhone(value)) return maskPhone(value) as string;
  return truncate(value, MAX_STRING_LEN) as string;
}

/**
 * Recursively sanitize an arbitrary record/array/primitive.
 * - Keys matching REDACT_KEY_RE → value replaced with [REDACTED]
 * - Strings that look like emails/phones/JWTs/bearer → masked
 * - Strings longer than MAX_STRING_LEN → truncated
 * - Depth > MAX_OBJECT_DEPTH → '[depth-limit]'
 * - Cycles → '[circular]'
 */
export function sanitize<T>(input: T): T {
  return sanitizeInner(input, 0, new WeakSet()) as T;
}

function sanitizeInner(value: unknown, depth: number, seen: WeakSet<object>, keyHint?: string): unknown {
  if (value == null) return value;
  if (depth > MAX_OBJECT_DEPTH) return '[depth-limit]';

  if (typeof value === 'string') return sanitizeString(value, keyHint);

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value;
  }

  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncate(value.message, 1024),
      stack: truncate(value.stack ?? null, MAX_STRING_LEN),
    };
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return '[circular]';
    seen.add(value);
    return value.map((v) => sanitizeInner(v, depth + 1, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value as object)) return '[circular]';
    seen.add(value as object);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (REDACT_KEY_RE.test(k)) {
        out[k] = REDACTED;
        continue;
      }
      out[k] = sanitizeInner(v, depth + 1, seen, k);
    }
    return out;
  }

  // Functions, symbols, etc. — drop.
  return undefined;
}
